import React, { useState, useEffect, useMemo } from 'react';
import api from '../api';
import { ChevronRight, MoreHorizontal, UserPlus, Pencil, Trash2, Check } from 'lucide-react';
import { useTasks } from '../context/TasksContext';
import { Sheet, Fab, SearchBox, Seg, ConfirmBox, Avatar, AvatarStack, avatarColor, plural, HUE } from '../components/AdminUi';

const nameOf = (m) => m.full_name || m.email;
const firstName = (m) => nameOf(m).split(/[\s@]/)[0];

function GroupTile({ name, size = 42 }) {
  const c = avatarColor(name);
  return <span className="adm-tile" style={{ width: size, height: size, fontSize: size * 0.43, background: `${c}1f`, color: c }}>{(name || '?')[0].toUpperCase()}</span>;
}

// Panel: nova grupa ili uređivanje naziva/opisa.
function GroupForm({ group, onCancel, onSaved }) {
  const [name, setName] = useState(group?.name || '');
  const [description, setDescription] = useState(group?.description || '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim()) return setError('Upiši naziv grupe.');
    setBusy(true);
    try {
      const res = group
        ? await api.put(`/groups/${group.id}`, { name: name.trim(), description })
        : await api.post('/groups', { name: name.trim(), description });
      onSaved(res.data?.id || group?.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Spremanje nije uspjelo.');
      setBusy(false);
    }
  };
  return (
    <>
      <div className="adm-fl">Naziv</div>
      <input id="grp-name" className="adm-input" value={name} onChange={e => { setName(e.target.value); setError(''); }} placeholder="npr. Vozni park" maxLength={30} />
      <div className="adm-fl">Opis</div>
      <input id="grp-desc" className="adm-input" value={description} onChange={e => setDescription(e.target.value)} placeholder="Čime se grupa bavi (nije obavezno)" maxLength={200} />
      {error && <p className="adm-err">{error}</p>}
      <div className="adm-two">
        <button type="button" className="adm-btn" onClick={onCancel}>Odustani</button>
        <button type="button" className="adm-btn primary" onClick={save} disabled={busy}>{group ? 'Spremi' : 'Dodaj grupu'}</button>
      </div>
    </>
  );
}

function GroupSheet({ group, me, isAdmin, stats, onClose, onChanged, onDeleted, onAddMembers, onShowTasks }) {
  const [menu, setMenu] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [personMenu, setPersonMenu] = useState(null);
  const [draft, setDraft] = useState({});           // email → 'owner' | 'member' | 'removed' (nespremljeno)
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const members = group.members || [];
  const stateOf = (m) => draft[m.email] || m.role;
  // Vlasnici/članovi po nacrtu; uklonjeni ostaju u svojoj izvornoj sekciji (precrtano) dok se ne spremi.
  const owners = members.filter(m => (draft[m.email] === 'removed' ? m.role : stateOf(m)) === 'owner');
  const plain = members.filter(m => (draft[m.email] === 'removed' ? m.role : stateOf(m)) !== 'owner');
  const changes = Object.keys(draft).length;

  const stage = (m, next) => {
    setDraft(prev => {
      const d = { ...prev };
      if (next === m.role) delete d[m.email]; else d[m.email] = next;
      return d;
    });
    setPersonMenu(null);
  };
  const save = async () => {
    setBusy(true); setError('');
    try {
      for (const [email, next] of Object.entries(draft)) {
        if (next === 'removed') await api.delete(`/groups/${group.id}/members/${encodeURIComponent(email)}`);
        else await api.post(`/groups/${group.id}/members`, { email, role: next });
      }
      await onChanged();
      setDraft({});
    } catch (err) {
      setError(err.response?.data?.error || 'Spremanje nije uspjelo.');
      await onChanged();
    } finally { setBusy(false); }
  };
  const removeGroup = async () => {
    setBusy(true);
    try { await api.delete(`/groups/${group.id}`); onDeleted(); }
    catch (err) { setError(err.response?.data?.error || 'Brisanje nije uspjelo.'); setBusy(false); }
  };

  const person = (m) => {
    const open = personMenu === m.email;
    const st = stateOf(m);
    const removed = st === 'removed';
    const pending = draft[m.email];
    return (
      <React.Fragment key={m.email}>
        <div className={`adm-it${removed ? ' is-removed' : ''}`}>
          <Avatar name={nameOf(m)} />
          <div className="adm-main">
            <div className="adm-t soft">{nameOf(m)}{m.email === me && <span className="me"> · ti</span>}</div>
            {pending
              ? <div className="adm-pending">{removed ? 'bit će uklonjen/a' : st === 'owner' ? 'postaje vlasnik' : 'postaje član'}</div>
              : m.full_name && <div className="adm-s">{m.email}</div>}
          </div>
          {isAdmin && (pending ? (
            <button type="button" className="adm-pbtn" onClick={() => stage(m, m.role)}>Vrati</button>
          ) : (
            <button type="button" className={`adm-iconbtn${open ? ' on' : ''}`} style={{ border: 'none', background: open ? undefined : 'none' }}
              onClick={() => setPersonMenu(open ? null : m.email)} title="Akcije">
              <MoreHorizontal size={20} />
            </button>
          ))}
        </div>
        {open && (
          <div className="adm-pop">
            {m.role === 'owner'
              ? <button type="button" className="adm-pbtn" onClick={() => stage(m, 'member')}>Postavi za člana</button>
              : <button type="button" className="adm-pbtn" onClick={() => stage(m, 'owner')}>Postavi za vlasnika</button>}
            <button type="button" className="adm-pbtn red" onClick={() => stage(m, 'removed')}>Ukloni iz grupe</button>
          </div>
        )}
      </React.Fragment>
    );
  };

  return (
    <Sheet tall onClose={onClose} dirty={changes > 0} busy={busy} discardText="Promjene na članovima nisu spremljene i bit će izgubljene."
      head={
        <div className="adm-sh">
          <GroupTile name={group.name} size={48} />
          <div className="adm-main">
            <h3>{group.name}</h3>
            {group.description && <p>{group.description}</p>}
          </div>
          {isAdmin && !editing && (
            <button type="button" className={`adm-iconbtn${menu ? ' on' : ''}`} onClick={() => setMenu(v => !v)} title="Uredi ili obriši grupu">
              <MoreHorizontal size={20} />
            </button>
          )}
        </div>
      }
      footer={changes > 0 && (
        <div className="adm-dirty">
          <div className="adm-two">
            <button type="button" className="adm-btn" disabled={busy}
              onClick={() => { setDraft({}); setError(''); }}>
              Odustani
            </button>
            <button type="button" className="adm-btn primary" disabled={busy} onClick={save}>
              Spremi ({changes})
            </button>
          </div>
        </div>
      )}
    >
      {menu && !editing && !confirmDel && (
        <div className="adm-card">
          <button type="button" className="adm-act" onClick={() => { setEditing(true); setMenu(false); }}><Pencil size={18} /> Uredi naziv i opis</button>
          <button type="button" className="adm-act red" onClick={() => { setConfirmDel(true); setMenu(false); }}><Trash2 size={18} /> Obriši grupu</button>
        </div>
      )}
      {confirmDel && (
        <ConfirmBox title="Obriši grupu?"
          text={`Grupa ${group.name} i njeno članstvo bit će obrisani. Zadaci ostaju, ali više neće biti vezani uz ovu grupu.`}
          onCancel={() => setConfirmDel(false)} onConfirm={removeGroup} busy={busy} />
      )}
      {editing && <GroupForm group={group} onCancel={() => setEditing(false)} onSaved={async () => { await onChanged(); setEditing(false); }} />}
      {error && <p className="adm-err">{error}</p>}

      <div className="adm-lbl" style={{ margin: '2px 2px -4px' }}>Zadaci {stats.year}.</div>
      <div className="adm-stats">
        <button type="button" className="adm-stat" onClick={onShowTasks}><b style={{ color: HUE.prog }}>{stats.running}</b><span>u tijeku</span></button>
        <button type="button" className="adm-stat" onClick={onShowTasks}><b style={{ color: HUE.pending }}>{stats.soon}</b><span>uskoro</span></button>
        <button type="button" className="adm-stat" onClick={onShowTasks}><b style={{ color: HUE.done }}>{stats.done}</b><span>završeno</span></button>
      </div>

      <div className="adm-lbl">Vlasnici · {owners.filter(m => stateOf(m) !== 'removed').length}</div>
      {owners.length ? <div className="adm-card">{owners.map(person)}</div> : <p className="adm-note">Grupa nema vlasnika.</p>}
      <div className="adm-lbl">Članovi · {plain.filter(m => stateOf(m) !== 'removed').length}</div>
      {plain.length ? <div className="adm-card">{plain.map(person)}</div> : <p className="adm-note">Nema ostalih članova.</p>}
      {isAdmin && (
        <button type="button" className="adm-btn" style={{ marginTop: 4 }} disabled={changes > 0} onClick={onAddMembers}><UserPlus size={18} /> Dodaj članove</button>
      )}
      {isAdmin && changes > 0 && <p className="adm-note">Spremi ili odustani od promjena prije dodavanja novih članova.</p>}
    </Sheet>
  );
}

function AddMembersSheet({ group, users, onClose, onDone }) {
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState(new Set());
  const [role, setRole] = useState('member');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inGroup = new Set((group.members || []).map(m => m.email));
  const ql = q.trim().toLowerCase();
  const list = users.filter(u => !inGroup.has(u.email) && (!ql || (u.full_name || '').toLowerCase().includes(ql) || u.email.toLowerCase().includes(ql)));
  const toggle = (email) => setPicked(prev => { const n = new Set(prev); n.has(email) ? n.delete(email) : n.add(email); return n; });
  const add = async () => {
    setBusy(true);
    try {
      await Promise.all([...picked].map(email => api.post(`/groups/${group.id}/members`, { email, role })));
      onDone();
    } catch (err) {
      setError(err.response?.data?.error || 'Dodavanje nije uspjelo.');
      setBusy(false);
    }
  };
  const n = picked.size;
  return (
    <Sheet tall onClose={onClose}
      head={<div className="adm-sh"><div className="adm-main"><h3>Dodaj u {group.name}</h3><p>Odaberi jednog ili više članova.</p></div></div>}
      footer={
        <button type="button" className="adm-btn primary" disabled={!n || busy} onClick={add}>
          {n ? `Dodaj ${n} ${role === 'owner' ? plural(n, 'vlasnika', 'vlasnika', 'vlasnika') : plural(n, 'člana', 'člana', 'članova')}` : 'Odaberi članove'}
        </button>
      }
    >
      <SearchBox value={q} onChange={setQ} placeholder="Pretraži po imenu ili e-mailu" />
      <Seg value={role} onChange={setRole} options={[{ value: 'member', label: 'kao član' }, { value: 'owner', label: 'kao vlasnik' }]} />
      {error && <p className="adm-err">{error}</p>}
      {list.length === 0 ? (
        <div className="adm-empty">{q ? 'Nitko ne odgovara pretrazi.' : 'Svi korisnici su već u grupi.'}</div>
      ) : (
        <div className="adm-card">
          {list.map(u => {
            const on = picked.has(u.email);
            return (
              <button key={u.email} type="button" className="adm-it" onClick={() => toggle(u.email)}>
                <Avatar name={u.full_name || u.email} />
                <div className="adm-main">
                  <div className="adm-t soft">{u.full_name || u.email}</div>
                  {u.full_name && <div className="adm-s">{u.email}</div>}
                </div>
                <span className={`adm-check${on ? ' on' : ''}`}>{on && <Check size={15} strokeWidth={3} />}</span>
              </button>
            );
          })}
        </div>
      )}
    </Sheet>
  );
}

function Groups({ user, onShowTasks, openRequest }) {
  const { tasksByYear, fetchYear } = useTasks();
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [sheet, setSheet] = useState(null);   // { type: 'group'|'add', id } | { type: 'new' }
  // Dolazak s Profila: otvori tu grupu
  useEffect(() => { if (openRequest?.id) setSheet({ type: 'group', id: openRequest.id }); }, [openRequest?.nonce]); // eslint-disable-line react-hooks/exhaustive-deps
  const isAdmin = user?.role === 'admin';
  const me = user?.email;
  const year = new Date().getFullYear();

  const fetchGroups = async () => {
    const res = await api.get('/groups');
    setGroups(res.data);
    return res.data;
  };
  useEffect(() => {
    fetchGroups();
    if (isAdmin) api.get('/users').then(res => setUsers(res.data));
    if (!tasksByYear[year]) fetchYear(year).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Brojevi zadataka po grupi (tekuća godina).
  const statsFor = useMemo(() => {
    const tasks = tasksByYear[year] || [];
    const now = Date.now();
    return (id) => {
      const s = { running: 0, soon: 0, done: 0, year };
      for (const t of tasks) {
        if (t.status === 'proposed' || t.is_container || !t.groups?.some(g => g.id === id)) continue;
        if (t.status === 'done') s.done++;
        else if (new Date(t.start_date).getTime() <= now) s.running++;
        else s.soon++;
      }
      return s;
    };
  }, [tasksByYear, year]);

  const isMine = (g) => (g.members || []).some(m => m.email === me);
  const mine = groups.filter(isMine);
  const others = groups.filter(g => !isMine(g));
  const current = sheet?.id ? groups.find(g => g.id === sheet.id) : null;

  const row = (g) => {
    const members = g.members || [];
    const owners = members.filter(m => m.role === 'owner');
    const my = members.find(m => m.email === me);
    const n = members.length;
    return (
      <button key={g.id} type="button" className="adm-it" onClick={() => setSheet({ type: 'group', id: g.id })}>
        <GroupTile name={g.name} />
        <div className="adm-main">
          <div className="adm-t">{g.name}</div>
          {g.description && <div className="adm-s">{g.description}</div>}
          <div className="adm-m">
            {n > 0 && <AvatarStack names={[...owners, ...members.filter(m => m.role !== 'owner')].map(nameOf)} />}
            <span>{n} {plural(n, 'član', 'člana', 'članova')}{owners.length ? ` · vlasnik ${owners.map(firstName).join(', ')}` : ''}</span>
          </div>
        </div>
        {my && <span className="adm-badge" style={my.role === 'owner' ? { background: '#6c5ce71a', color: HUE.violet } : { background: '#3f8f6b1a', color: HUE.done }}>{my.role === 'owner' ? 'vlasnik' : 'član'}</span>}
        <ChevronRight size={18} className="adm-chev" />
      </button>
    );
  };

  return (
    <div className="adm-page">
      <div className="adm-head"><h2>Grupe</h2><span className="n">{groups.length}</span></div>
      {groups.length === 0 && <div className="adm-empty">{isAdmin ? 'Još nema grupa. Dodaj prvu gumbom +.' : 'Nisi član nijedne grupe.'}</div>}
      {mine.length > 0 && <><div className="adm-lbl">Tvoje grupe</div><div className="adm-card">{mine.map(row)}</div></>}
      {others.length > 0 && <><div className="adm-lbl">Ostale grupe</div><div className="adm-card">{others.map(row)}</div></>}

      {isAdmin && <Fab onClick={() => setSheet({ type: 'new' })} title="Nova grupa" />}

      {sheet?.type === 'new' && (
        <Sheet onClose={() => setSheet(null)} head={<div className="adm-sh"><div className="adm-main"><h3>Nova grupa</h3><p>Ti postaješ vlasnik; ostale dodaješ nakon toga.</p></div></div>}>
          <GroupForm onCancel={() => setSheet(null)} onSaved={async (id) => { await fetchGroups(); setSheet(id ? { type: 'group', id } : null); }} />
        </Sheet>
      )}
      {sheet?.type === 'group' && current && (
        <GroupSheet key={current.id} group={current} me={me} isAdmin={isAdmin} stats={statsFor(current.id)}
          onClose={() => setSheet(null)}
          onChanged={fetchGroups}
          onDeleted={() => { setSheet(null); fetchGroups(); }}
          onAddMembers={() => setSheet({ type: 'add', id: current.id })}
          onShowTasks={() => { setSheet(null); onShowTasks && onShowTasks({ groups: [current.id] }); }} />
      )}
      {sheet?.type === 'add' && current && (
        <AddMembersSheet group={current} users={users} onClose={() => setSheet({ type: 'group', id: current.id })}
          onDone={async () => { await fetchGroups(); setSheet({ type: 'group', id: current.id }); }} />
      )}
    </div>
  );
}

export default Groups;
